import { Injectable } from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {PartnerBannerDgSearchResults} from "../models/datagrid-search-results/partner-banner-dg-search-results.model";
import {PartnerBanner} from "../models/partner-banner.model";
import {Snapshot} from "../models/snpashot.model";

/**
 * Serwis warstwy danych zarządzający przestrzenią reklamową i promocyjną (Banerami)
 * udostępnianą Partnerom wewnątrz platformy.
 * Implementuje standardowy interfejs DataGrid do obsługi widoków tabelarycznych
 * oraz udostępnia pełen cykl operacji CRUD dla kampanii banerowych.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerBannersService implements DataGridServiceInterface<Observable<PartnerBannerDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) { }

  /**
   * Pobiera paginowaną listę banerów reklamowych dla widoku tabeli (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania wyników (np. ASC/DESC).
   * @param orderBy Nazwa kolumny, według której aplikowane jest sortowanie.
   * @param showDeletedObjects Flaga decydująca o uwzględnieniu zarchiwizowanych banerów.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla komponentu tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerBannerDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-banners/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerBannerDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerBanner(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje filtrowanie i wyszukiwanie banerów w oparciu o zaawansowane kryteria
   * (np. nazwa kampanii, przypisany partner, status aktywności).
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych obiektów.
   * @returns Observable emitujący przefiltrowaną listę wyników DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerBannerDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/partner-banners/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerBannerDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new PartnerBanner(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje modyfikacje w istniejącej konfiguracji banera.
   *
   * @param partnerBannerEdited Zmodyfikowany obiekt banera.
   * @returns Observable emitujący zaktualizowany obiekt zwrócony z serwera.
   */
  updatePartnerBanner(partnerBannerEdited: PartnerBanner): Observable<PartnerBanner> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/partner-banners', partnerBannerEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<PartnerBanner>(new PartnerBanner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy nowy baner reklamowy partnera.
   *
   * @param partnerBanner Obiekt zawierający dane nowego banera.
   * @returns Observable emitujący utworzony obiekt.
   */
  addPartnerBanner(partnerBanner: PartnerBanner): Observable<PartnerBanner> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/partner-banners`, partnerBanner).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<PartnerBanner>(new PartnerBanner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dziennik audytu (historię zmian) dla określonego banera.
   *
   * @param uuid Unikalny identyfikator banera.
   * @returns Observable emitujący tablicę migawek (Snapshot) z historią.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-banners/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa wskazany baner reklamowy.
   *
   * @param partnerBannerUUID Identyfikator banera przeznaczonego do usunięcia.
   * @returns Observable emitujący stan usuniętego obiektu z backendu.
   */
  deletePartner(partnerBannerUUID: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/partner-banners/` + partnerBannerUUID).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<PartnerBanner>(new PartnerBanner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
